import { useEffect, useRef } from 'react';
import { useTranslation } from '@frontend/i18n';
import { SmsMessageNotification } from '@frontend/types';
import { useNotificationPreferences } from './store';

const NOTIFICATION_TYPE = 'sms-message-new';
const PREFERENCE_ID = 'sms-reminder-after-48h';

export const useAutoPauseSmsWithReminder = () => {
  const { t } = useTranslation('notifications');
  const {
    pauseNotificationType,
    addSuppressRule,
    addPreferenceRule,
    isNotificationPaused,
    hasPreferenceDismissed,
    suppressedNotificationIds,
    setSuppressedNotificationIds,
  } = useNotificationPreferences([
    'pauseNotificationType',
    'addSuppressRule',
    'addPreferenceRule',
    'isNotificationPaused',
    'hasPreferenceDismissed',
    'suppressedNotificationIds',
    'setSuppressedNotificationIds',
  ]);
  const suppressedNotificationIdsRef = useRef<Record<string, boolean>>(suppressedNotificationIds);

  useEffect(() => {
    const unsubscribeSuppressRule = addSuppressRule({
      id: PREFERENCE_ID,
      for: NOTIFICATION_TYPE,
      condition: (notification, context) => {
        const smsNotification = notification as SmsMessageNotification;

        const isAtThread = context.pathname?.startsWith(
          `/messages/inbox/${smsNotification.payload.locationId}/${smsNotification.payload.threadId}`
        );
        // Exit early if not in inbox
        if (!isAtThread || !document.hasFocus()) return false;

        // If already dismissed, just return pause status
        if (hasPreferenceDismissed(PREFERENCE_ID)) {
          return isNotificationPaused(NOTIFICATION_TYPE);
        }

        // Auto pause notifications
        pauseNotificationType(NOTIFICATION_TYPE, true);
        suppressedNotificationIdsRef.current = {
          ...suppressedNotificationIdsRef.current,
          [(notification.payload as { notificationID: string }).notificationID]: true,
        };
        setSuppressedNotificationIds(suppressedNotificationIdsRef.current);

        return true;
      },
    });

    return () => {
      unsubscribeSuppressRule();
    };
  }, [addSuppressRule, addPreferenceRule, isNotificationPaused, t]);
};
